/**
 * USER RESTRICTION HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • getOwnUserRestriction()  - GET /kyc-verification/restriction
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useUserRestriction(enabled?)   - Query: Get authenticated user's restriction type
 *
 * ===========================================
 * HELPERS (Exported):
 * ===========================================
 * • useRestrictionGate()  - Gate hook for entry-point components
 * • getRestrictionModalVariant - see @/lib/restriction-utils
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type React from 'react';
import { useState } from 'react';
import { getOwnUserRestriction } from '@/actions/restriction';
import { getRestrictionModalVariant } from '@/lib/restriction-utils';
import {
  RESTRICTION_MODAL_VARIANTS,
  type RestrictionModalVariant,
  type UserRestriction,
} from '@/types/restriction';

// ==================== REACT QUERY HOOK ====================

/**
 * Hook to fetch the authenticated user's compliance restriction type.
 * Fails open (returns null) when the query is loading or errored so the UI
 * never blocks an action just because the API is slow.
 */
export function useUserRestriction(
  enabled: boolean = true
): UseQueryResult<UserRestriction | null, Error> {
  return useQuery({
    queryKey: ['user-restriction'],
    queryFn: getOwnUserRestriction,
    enabled,
    staleTime: 120000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

// ==================== GATE HOOK ====================

interface RestrictionGate {
  /** True when the user has an active restricting gate (aml / block / final / first_rejection). */
  isRestricted: boolean;
  /**
   * Wrap an onClick action. If the user is restricted, opens the dialog instead.
   * Otherwise calls the action.
   */
  guard: (action: () => void) => void;
  /**
   * Use as an onClick on a <Link>. Calls preventDefault and opens the dialog
   * when restricted. Returns true if it blocked navigation.
   */
  guardNavigation: (event: React.MouseEvent) => boolean;
  /** Spread these onto <RestrictionDialog />. */
  restrictionDialogProps: {
    variant: RestrictionModalVariant;
    open: boolean;
    onOpenChange: (open: boolean) => void;
  };
}

export function useRestrictionGate(): RestrictionGate {
  const [open, setOpen] = useState(false);
  const { data: restriction } = useUserRestriction();

  const variant = getRestrictionModalVariant(restriction);
  const isRestricted = variant !== RESTRICTION_MODAL_VARIANTS.NONE;

  const guard = (action: () => void) => {
    if (isRestricted) {
      setOpen(true);
    } else {
      action();
    }
  };

  const guardNavigation = (event: React.MouseEvent): boolean => {
    if (isRestricted) {
      event.preventDefault();
      setOpen(true);
      return true;
    }
    return false;
  };

  return {
    isRestricted,
    guard,
    guardNavigation,
    restrictionDialogProps: {
      variant,
      open,
      onOpenChange: setOpen,
    },
  };
}
